/*
 * Contact page
 *
 * Responsibilities:
 * - Fetch and display contact information and facility metadata from the backend
 *   endpoints: '/contact-info/' and '/facility/'.
 * - Provide convenient actions for users: call, email, copy-to-clipboard, and
 *   open social links.
 * - Render structured hours if provided by the backend; otherwise show the
 *   built-in sample schedule.
 */

import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Grid,
  Button,
  Box,
  Divider
} from '@mui/material';
import { styled } from '@mui/system';
import EmailIcon from '@mui/icons-material/Email';
import MailIcon from '@mui/icons-material/Mail';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CallIcon from '@mui/icons-material/Call';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { IconButton, Tooltip, Snackbar, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material';
import api from '../api';
import { Helmet } from 'react-helmet';

// Responsive hero image with multiple sizes
const heroBase = 'https://res.cloudinary.com/dmu7vt5cs/image/upload/v1764552855/contact-hero_c1mbj0.jpg';
const heroSrcSet = [
  `https://res.cloudinary.com/dmu7vt5cs/image/upload/f_auto,q_auto,w_640,c_limit/${heroBase.split('/upload/')[1]} 640w`,
  `https://res.cloudinary.com/dmu7vt5cs/image/upload/f_auto,q_auto,w_750,c_limit/${heroBase.split('/upload/')[1]} 750w`,
  `https://res.cloudinary.com/dmu7vt5cs/image/upload/f_auto,q_auto,w_1080,c_limit/${heroBase.split('/upload/')[1]} 1080w`,
  `https://res.cloudinary.com/dmu7vt5cs/image/upload/f_auto,q_auto,w_1366,c_limit/${heroBase.split('/upload/')[1]} 1366w`,
].join(', ');
const heroFallback = `https://res.cloudinary.com/dmu7vt5cs/image/upload/f_auto,q_auto,w_1080,c_limit/${heroBase.split('/upload/')[1]}`;


const InfoItem = styled(Box)(() => ({
  marginBottom: '1rem',
  display: 'flex',
  alignItems: 'center',
  color: '#9BC9F5'
}));

/*
  InfoItem
  - Small styled wrapper used for each contact row (location, phone, email, socials)
*/

function ContactUs() {
  const [contact, setContact] = useState(null);
  const [facility, setFacility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmNumber, setConfirmNumber] = useState('');

  const handleCopy = (text) => {
    // Copy the provided text to the clipboard and show a snackbar for feedback
    if (!text) return;
    const toCopy = String(text).trim();
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(toCopy).then(() => {
        setSnackbarMessage('Copied to clipboard');
        setSnackbarOpen(true);
      }).catch(() => {
        setSnackbarMessage('Copy failed');
        setSnackbarOpen(true);
      });
    } else {
      try {
        // fallback
        const el = document.createElement('textarea');
        el.value = toCopy;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
        setSnackbarMessage('Copied to clipboard');
        setSnackbarOpen(true);
      } catch (e) {
        setSnackbarMessage('Copy failed');
        setSnackbarOpen(true);
      }
    }
  };


  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // Facility contains location. Use centralized api so tests can mock requests.
    Promise.all([
      api.get('/contact-info/').then(r => (r && r.data) ? r.data : []),
      api.get('/facility/').then(r => (r && r.data) ? r.data : [])
    ])
      .then(([contactData, facilityData]) => {
        if (cancelled) return;
        setContact(contactData && contactData.length ? contactData[0] : null);
        setFacility(facilityData && facilityData.length ? facilityData[0] : null);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err);
        setLoading(false);
      });
    // Cleanup: mark cancelled so resolved promises won't call setState after unmount
    return () => { cancelled = true; };
  }, []);


  // Extract a username/handle from a social url (instagram/facebook).
  // If parsing fails return the raw URL so the UI can still link to it.
    const extractHandle = (url) => {
      if (!url) return '';
      try {
        const u = new URL(url);
        const parts = u.pathname.split('/').filter(Boolean);
        const last = parts.length ? parts[parts.length - 1] : u.hostname;
        const clean = last.replace(/\?.*$/, '').replace(/\/$/, '');
        return clean.startsWith('@') ? clean : `@${clean}`;
      } catch (e) {
        return url;
      }
    };
    /*
      renderStructuredHours
      - Accepts the structured hours object
      - Returns an array of <Box> rows with day label + times ready for rendering
      - Expected input: { regular: { monday: [["09:00","17:00"]], ... }, exceptions: [...] }
    */
    const renderStructuredHours = (hoursObj) => {
      if (!hoursObj) return null;
      const regular = hoursObj.regular || hoursObj;
      const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      return dayOrder.map((dayKey) => {
        const intervals = regular[dayKey] || [];
        const displayDay = dayKey.charAt(0).toUpperCase() + dayKey.slice(1);
        const times = intervals && intervals.length ? intervals.map(i => `${i[0]}–${i[1]}`).join(', ') : 'CLOSED';
        return (
          <Box key={dayKey} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 1 }}>
            <Typography sx={{ minWidth: 160, fontWeight: 'bold' }}>{displayDay}</Typography>
            <Typography sx={{ whiteSpace: 'pre-wrap' }}>{times}</Typography>
          </Box>
        );
      });
    };
    // Prepare hoursContent to render below.
    // Compute ahead of the JSX return to keep the returned markup simple and readable.
    let hoursContent = null;
    if (contact?.hours) {
      // If the backend returns structured JSON, render via renderStructuredHours.
      if (typeof contact.hours === 'object') {
        hoursContent = renderStructuredHours(contact.hours);
      }
    } else {
      hoursContent = (
        <>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 1 }}>
            <Typography sx={{ minWidth: 160, fontWeight: 'bold' }}>Monday–Thursday</Typography>
            <Typography>1:00PM–2:00PM, 5:00PM–9:30PM</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 1 }}>
            <Typography sx={{ minWidth: 160, fontWeight: 'bold' }}>Friday</Typography>
            <Typography>1:00PM–2:00PM, 5:30PM–9:00PM</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 1 }}>
            <Typography sx={{ minWidth: 160, fontWeight: 'bold' }}>Saturday</Typography>
            <Typography>12:00PM–2:00PM</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Typography sx={{ minWidth: 160, fontWeight: 'bold' }}>Sunday</Typography>
            <Typography>CLOSED</Typography>
          </Box>
        </>
      );
    }
  return (
  <Container maxWidth="xl" sx={{ backgroundColor: '#111', color: '#fff', py: 6 }}>
      <Helmet>
        <title>Contact & Location — Kingdom MMA Edmonton</title>
        <meta name="description" content="Get in touch with Kingdom MMA — phone, email, hours, and directions to our Edmonton gym. Book a 3‑day free trial or ask about membership plans." />
        <link rel="canonical" href="https://www.kingdommixedmartialarts.ca/contact-us/" />
        <meta property="og:title" content="Contact — Kingdom MMA and Fitness" />
        <meta property="og:description" content="Find our address, hours, and contact details for Kingdom MMA and Fitness in Edmonton." />
        <meta property="og:url" content="https://www.kingdommixedmartialarts.ca/contact-us/" />
  <meta property="og:image" content="https://www.kingdommixedmartialarts.ca/logo-no-text.svg" />
      </Helmet>
      {/* Hero image */}
      <Box
        component="img"
        src={heroFallback}
        srcSet={heroSrcSet}
        sizes="(max-width: 600px) 100vw, (max-width: 960px) 100vw, (max-width: 1280px) 1280px, 1366px"
        alt="Kingdom Martial Arts gym interior"
        loading="eager"
        fetchpriority="high"
        width="1366"
        height="573"
        sx={{
          width: '100%',
          height: { xs: 160, md: 320 },
          objectFit: 'cover',
          objectPosition: 'center 20%',
          borderRadius: 2,
          mb: 4,
          display: 'block',
          boxShadow: '0 6px 18px rgba(0,0,0,0.6)'
        }}
      />

      <Typography variant="h3" align="left" sx={{ mb: 5, fontWeight: 'bold' }}>
        Contact Us
      </Typography>

      <Grid container spacing={4} justifyContent="center">
        {/* Gym Info */}
        <Grid item xs={12} md={8}>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
            Gym Information
          </Typography>


          {loading ? (
            <Typography>Loading contact information...</Typography>
          ) : error ? (
            <Typography color="error">Unable to load contact information.</Typography>
          ) : (
            <>
              <InfoItem>
                <LocationOnIcon sx={{ mr: 1 }} />
                <Typography>{facility?.location || contact?.location || '9785 45 Ave NW, Edmonton, AB'}</Typography>
              </InfoItem>
              <InfoItem>
                <PhoneIcon sx={{ mr: 1 }} />
                {(() => {
                  const phoneRaw = contact?.phone || '(825)-512-5343';
                  const phoneHref = phoneRaw ? phoneRaw.replace(/[^+\d]/g, '') : '';
                  const handleCallClick = (e) => {
                    // On mobile, allow direct dial. On desktop, show confirmation dialog.
                    const isMobile = /Mobi|Android/i.test(navigator.userAgent || '');
                    if (!isMobile) {
                      e.preventDefault();
                      setConfirmNumber(phoneHref);
                      setConfirmOpen(true);
                    }
                    // if mobile, let the anchor proceed
                  };

                  return (
                    <Typography component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <a
                        href={phoneHref ? `tel:${phoneHref}` : undefined}
                        onClick={handleCallClick}
                        style={{ color: '#9BC9F5', textDecoration: 'none' }}
                        aria-label={`Call ${phoneRaw}`}
                      >
                        {phoneRaw}
                      </a>

                      <Tooltip title="Copy number">
                        <IconButton size="small" onClick={() => handleCopy(phoneRaw)} sx={{ color: '#9BC9F5' }}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Call">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            // open call directly on mobile, or show confirm on desktop
                            const isMobile = /Mobi|Android/i.test(navigator.userAgent || '');
                            if (isMobile) {
                              window.location.href = `tel:${phoneHref}`;
                            } else {
                              setConfirmNumber(phoneHref);
                              setConfirmOpen(true);
                            }
                          }}
                          sx={{ color: '#9BC9F5' }}
                          aria-label={`Call ${phoneRaw}`}
                        >
                          <CallIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Typography>
                  );
                })()}
              </InfoItem>
              <InfoItem>
                <EmailIcon sx={{ mr: 1 }} />
                {(() => {
                  const email = contact?.email || 'kingdomcombatsports@gmail.com';
                  const emailHref = `mailto:${email}`;
                  return (
                    <Typography component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <a href={emailHref} style={{ color: '#9BC9F5', textDecoration: 'none' }} aria-label={`Email ${email}`}>
                        {email}
                      </a>

                      <Tooltip title="Copy email">
                        <IconButton size="small" onClick={() => handleCopy(email)} sx={{ color: '#9BC9F5' }}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Send email">
                        <IconButton
                          size="small"
                          onClick={() => { window.location.href = emailHref; }}
                          sx={{ color: '#9BC9F5' }}
                          aria-label={`Email ${email}`}
                        >
                          <MailIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Typography>
                  );
                })()}
              </InfoItem>

              {contact?.instagram && (
                <InfoItem>
                  <InstagramIcon sx={{ mr: 1 }} />
                  <Typography component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <a
                      href={contact.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#9BC9F5', textDecoration: 'none' }}
                    >
                      {extractHandle(contact.instagram)}
                    </a>
                    <Tooltip title="Open Instagram">
                      <IconButton size="small" onClick={() => window.open(contact.instagram, '_blank')} sx={{ color: '#9BC9F5' }}>
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Typography>
                </InfoItem>
              )}

              {contact?.facebook && (
                <InfoItem>
                  <FacebookIcon sx={{ mr: 1 }} />
                  <Typography component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <a
                      href={contact.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#9BC9F5', textDecoration: 'none' }}
                    >
                      {extractHandle(contact.facebook)}
                    </a>
                    <Tooltip title="Open Facebook">
                      <IconButton size="small" onClick={() => window.open(contact.facebook, '_blank')} sx={{ color: '#9BC9F5' }}>
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Typography>
                </InfoItem>
              )}
              <Typography component="span" variant="body2" color="textSecondary" sx={{ ml: 1 }}>
                Email is the best method of contact
              </Typography>

          {/* Confirmation dialog shown on desktop before initiating a call */}
          <Dialog open={confirmOpen || false} onClose={() => setConfirmOpen(false)}>
            <DialogTitle>Start a call?</DialogTitle>
            <DialogContent>
              <Typography>Do you want to call {confirmNumber ? confirmNumber : ''}?</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
              <Button
                onClick={() => {
                  setConfirmOpen(false);
                  if (confirmNumber) window.location.href = `tel:${confirmNumber}`;
                }}
                autoFocus
              >
                Call
              </Button>
            </DialogActions>
          </Dialog>

          <Snackbar open={snackbarOpen || false} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)}>
            <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
              {snackbarMessage}
            </Alert>
          </Snackbar>
            </>
          )}

          

          <Box sx={{ mt: 2 }}>
            {/**
             * Use facility.location when available to center the embedded map.
             * Google Maps query embed (no API key)
             */}
            {(() => {
              const address = facility?.location || '9785 45 Ave NW, Edmonton, AB T6E 5V8';
              const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(address)}&output=embed`;
              return (
                <iframe
                  title="Kingdom Martial Arts Location"
                  src={mapSrc}
                  width="100%"
                  height="300"
                  style={{
                    border: 0,
                    borderRadius: '10px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                  }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              );
            })()}
          </Box>

          <Divider sx={{ my: 3, borderColor: '#555' }} />

          <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
            Hours of Operation
          </Typography>

          <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left' }}>
            {hoursContent}
          </Box>

          <Typography variant="body2" color="textSecondary">
            The gym is closed on statutory holidays.
          </Typography>
        </Grid>
      </Grid>
    </Container>
  );
}

export default ContactUs;
